import { CancelCircleIcon, CheckmarkCircle01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, InputAdornment, Stack, SxProps, TextField, Typography, useTheme } from "@mui/material";
import { useResponsive } from "@/hooks";
import { useLazyFetchOrganizationAliasQuery } from "@/features/organizations/api/websiteApi";
import { isEmpty } from "lodash";

interface Props {
  value?: string;
  isVerified?: boolean;
  onChange?: (value: string) => void;
  onSuccess?: (value: string) => void;
  onFail?: (value: string) => void;
  sx?: SxProps;
}

const Alias = ({ value = "", isVerified = false, onChange, onSuccess, onFail, sx }: Props) => {
  const currentValue = useRef<string | null>(null);
  const [isValid, setIsValid] = useState(false);
  const { isExtraSmall } = useResponsive();
  const { palette } = useTheme();

  const [fetchAlias, { isFetching }] = useLazyFetchOrganizationAliasQuery();

  const [debounce, setDebounce] = useState(
    setTimeout(() => {
      return;
    }, 500)
  );

  const checkAvailability = async (alias: string) => {
    if (!alias) return;

    const result = await fetchAlias(alias);

    if (result.data) {
      setIsValid(false);
      onFail?.(alias);
    } else if (result.error) {
      if ((result.error as { status?: number })?.status === 404) {
        setIsValid(true);
        onSuccess?.(alias);
      } else if (!isVerified) {
        setIsValid(false);
        onFail?.(alias);
      }
    }
  };

  const handleChange = (alias: string) => {
    const value = alias
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z-]/g, "");

    if (currentValue.current === null) {
      currentValue.current = value;
    }

    onChange?.(value);

    if (debounce) {
      clearTimeout(debounce);
    }

    setDebounce(
      setTimeout(() => {
        checkAvailability(value);
      }, 500)
    );
  };

  useEffect(() => {
    if (!isEmpty(value)) {
      handleChange(value);
    }

    return () => {
      currentValue.current = null;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack alignItems="center" sx={sx}>
      <Stack
        direction={isExtraSmall ? "column" : "row"}
        alignItems="stretch"
        sx={sx}
      >
        <Box
          className="field-container"
          sx={{
            display: "flex",
            alignItems: "center",
            paddingX: 3,
            ...sx,
          }}
        >
          <Typography variant="body1" sx={sx}>
            http://bitelink.app/
          </Typography>
        </Box>
        <TextField
          className="alias-input"
          sx={{
            ...sx,
          }}
          value={value}
          onChange={(e: { target: { value: string } }) => handleChange(e.target.value)}
          autoFocus
          slotProps={{
            input: {
              endAdornment:
                value === "" ? undefined : (
                  <InputAdornment position="end">
                    {isFetching ? (
                      <CircularProgress color="secondary" size={24} />
                    ) : isValid || value === currentValue.current ? (
                      <HugeiconsIcon icon={CheckmarkCircle01Icon} color={palette.success.main} />
                    ) : (
                      <HugeiconsIcon icon={CancelCircleIcon} color={palette.error.main} />
                    )}
                  </InputAdornment>
                ),
            },
          }}
        />
      </Stack>
    </Stack>
  );
};

export default Alias;
